import {useCallback, useEffect, useRef} from 'react';
import {useVideoRecording} from './useVideoRecording';
import {LivenessAction} from '@chekin/core';
import {useLivenessStore, useProgressStore} from '../store';
import {useAdaptiveFaceDetection, type FacePosition} from '../../faceDetection';
import {useLivenessChallengeTracking} from './useLivenessChallengeTracking';
import {LIVENESS_BLINK_TARGET} from '../constants';
import {WebcamRefTypes} from '@chekinapp/ui';

const actions: LivenessAction[] = ['TURN_LEFT', 'TURN_RIGHT', 'TURN_UP'];
const generateRandomAction = (): LivenessAction => {
  const randomIndex = Math.floor(Math.random() * actions.length);
  return actions[randomIndex];
};

type UseLivenessDetectionProps = {
  forceLivenessMechanism: 'AUTO' | 'SERVER' | 'CLIENT';
};
export const useLivenessDetection = ({
  forceLivenessMechanism,
}: UseLivenessDetectionProps) => {
  const videoRef = useRef<WebcamRefTypes>(null);
  const animationRef = useRef<number | null>(null);
  const screenshotTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const generateRandomActionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const blinkStepTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const completeLivenessDetectionRef = useRef<NodeJS.Timeout | null>(null);

  const {onSignal} = useLivenessChallengeTracking();

  const {isModelLoading, facePosition, startDetection, isUsingClientDetection} =
    useAdaptiveFaceDetection({
      videoRef,
      forceMechanism: forceLivenessMechanism || 'AUTO',
      onSignal,
    });

  const {isRecording, startRecording, stopRecording} = useVideoRecording({
    videoRef,
    onTimeExceeded: () => {},
  });

  const facePositionRef = useRef<FacePosition>();

  const currentStep = useLivenessStore(state => state.currentStep);
  const stepStatus = useLivenessStore(state => state.stepStatus);
  const randomAction = useLivenessStore(state => state.currentChallenge);
  const faceScreenshot = useLivenessStore(state => state.faceScreenshot);
  const blinkCount = useLivenessStore(state => state.blinkCount);
  const completedActions = useLivenessStore(state => state.completedActions);
  const setCurrentStep = useLivenessStore(state => state.setCurrentStep);
  const setStepStatus = useLivenessStore(state => state.setStepStatus);
  const setFaceScreenshot = useLivenessStore(state => state.setFaceScreenshot);
  const setCurrentChallenge = useLivenessStore(state => state.setCurrentChallenge);
  const resetState = useLivenessStore(state => state.resetState);
  const resetProgressState = useProgressStore(state => state.resetState);
  const setCompletedActions = useLivenessStore(state => state.setCompletedActions);
  facePositionRef.current = facePosition;

  const hasBlinkedEnough = blinkCount >= LIVENESS_BLINK_TARGET;

  const isCorrectPosition = useCallback(() => {
    if (!randomAction || !facePosition) return false;

    switch (randomAction) {
      case 'TURN_LEFT':
        return facePosition === 'left';
      case 'TURN_RIGHT':
        return facePosition === 'right';
      case 'TURN_UP':
        return facePosition === 'up';
      case 'BLINK':
        return hasBlinkedEnough;
      default:
        return false;
    }
  }, [randomAction, facePosition, hasBlinkedEnough]);

  useEffect(
    function setUserCompletedActions() {
      const currentAction = currentStep as LivenessAction;
      if (
        [...actions, 'BLINK'].includes(currentAction) &&
        !completedActions.includes(currentAction)
      ) {
        setCompletedActions([...completedActions, currentAction]);
      }
    },
    [currentStep, completedActions, setCompletedActions],
  );

  const resetAll = useCallback(() => {
    resetState();
    resetProgressState();
  }, [resetState, resetProgressState]);

  const startLivenessDetection = useCallback(() => {
    resetAll();
    setStepStatus('progress');
    startRecording();

    const runFaceDetection = () => {
      startDetection();
      animationRef.current = requestAnimationFrame(runFaceDetection);
    };

    runFaceDetection();
  }, [resetAll, setStepStatus, startRecording, startDetection]);

  const completeLivenessDetection = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
      completeLivenessDetectionRef.current = null;
    }
    setStepStatus('success');

    setTimeout(() => {
      stopRecording();
    }, 300);

    completeLivenessDetectionRef.current = setTimeout(() => {
      setCurrentStep('COMPLETED');

      setTimeout(() => {
        resetAll();
      }, 2000);
    }, 1000);
  }, [stopRecording, setCurrentStep, setStepStatus, resetAll]);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      if (isRecording) {
        stopRecording();
      }
    };
  }, [isRecording, stopRecording]);

  useEffect(() => {
    return () => {
      if (screenshotTimeoutRef.current) {
        clearTimeout(screenshotTimeoutRef.current);
      }
      if (generateRandomActionTimeoutRef.current) {
        clearTimeout(generateRandomActionTimeoutRef.current);
      }
      if (blinkStepTimeoutRef.current) {
        clearTimeout(blinkStepTimeoutRef.current);
      }
      if (completeLivenessDetectionRef.current) {
        clearTimeout(completeLivenessDetectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  const takeScreenshot = useCallback(() => {
    if (!videoRef.current) return;

    if (screenshotTimeoutRef.current) {
      clearTimeout(screenshotTimeoutRef.current);
    }

    if (generateRandomActionTimeoutRef.current) {
      clearTimeout(generateRandomActionTimeoutRef.current);
    }

    screenshotTimeoutRef.current = setTimeout(async () => {
      if (
        currentStep === 'LOOK_STRAIGHT' &&
        stepStatus === 'progress' &&
        facePositionRef.current === 'center'
      ) {
        const screenshot = await videoRef.current?.takePhoto();
        if (!screenshot) return;
        setFaceScreenshot(screenshot);
        setStepStatus('success');
        generateRandomActionTimeoutRef.current = setTimeout(() => {
          const nextAction = generateRandomAction();
          setCurrentChallenge(nextAction);
          setCurrentStep(nextAction);
          setStepStatus('progress');
        }, 1500);
      } else if (currentStep === 'LOOK_STRAIGHT' && stepStatus === 'progress') {
        setStepStatus('error');
        setTimeout(() => {
          setStepStatus('progress');
        }, 1000);
      }

      screenshotTimeoutRef.current = null;
    }, 1600);
  }, [
    setStepStatus,
    setCurrentChallenge,
    setCurrentStep,
    setFaceScreenshot,
    currentStep,
    stepStatus,
  ]);

  useEffect(() => {
    if (isModelLoading) return;

    const checkStatus = () => {
      if (currentStep === 'LOOK_STRAIGHT' && stepStatus === 'progress') {
        if (facePosition === 'center') {
          takeScreenshot();
        }
      } else if (
        ['TURN_LEFT', 'TURN_RIGHT', 'TURN_UP'].includes(currentStep) &&
        stepStatus === 'progress'
      ) {
        if (isCorrectPosition()) {
          setStepStatus('success');
          if (blinkStepTimeoutRef.current) {
            clearTimeout(blinkStepTimeoutRef.current);
          }
          blinkStepTimeoutRef.current = setTimeout(() => {
            setCurrentChallenge('BLINK');
            setCurrentStep('BLINK');
            setStepStatus('progress');
          }, 1500);
        } else if (
          facePosition !== 'unknown' &&
          facePosition !== 'center' &&
          randomAction &&
          ((randomAction === 'TURN_LEFT' && facePosition !== 'left') ||
            (randomAction === 'TURN_RIGHT' && facePosition !== 'right') ||
            (randomAction === 'TURN_UP' && facePosition !== 'up'))
        ) {
          // Wrong direction detected
          setStepStatus('error');
          errorTimeoutRef.current = setTimeout(() => {
            setStepStatus('progress');
          }, 1000);
        } else {
          setStepStatus('progress');
        }
      } else if (currentStep === 'BLINK' && stepStatus === 'progress') {
        if (hasBlinkedEnough) {
          setStepStatus('success');
          completeLivenessDetection();
        }
      }
    };

    checkStatus();
  }, [
    isModelLoading,
    currentStep,
    stepStatus,
    facePosition,
    hasBlinkedEnough,
    isCorrectPosition,
    takeScreenshot,
    completeLivenessDetection,
    randomAction,
    setStepStatus,
    setCurrentStep,
    setCurrentChallenge,
  ]);

  return {
    videoRef,
    facePosition,
    isModelLoading,
    isRecording,
    startLivenessDetection,
    stopRecording,
    faceScreenshot,
    isUsingClientDetection,
  };
};
